import { inject, Injectable } from '@angular/core';
import { AuthorizationService } from '@myarvato/core/authentication';
import { Iso3166ToCountryPipe } from '@myarvato/shared/pipelines';
import {
  BooleanColumnDefinition,
  BooleanFilterInfo,
  ColumnDefinitionBase,
  CountryColumnDefinition,
  DateFilterInfo,
  DateTimeColumnDefinition,
  DirectionColumnDefinition,
  LazyMultiSelectFilterInfo,
  MultiSelectFilterInfo,
  MultiTextFilterInfo,
  NumberColumnDefinition,
  NumericFilterInfo,
  SelectFilterOption,
  SortInfo,
  TextColumnDefinition,
  TrafficLightColumnDefinition,
  WeightColumnDefinition,
} from '@myarvato/shared/table';
import { TranslateService } from '@ngx-translate/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { TransportTrafficLightRuleConfig } from '@myarvato/modules/transport/models';
import { FilterOptionsService } from './filter-options.service';

@Injectable({
  providedIn: 'root',
})
export class ColumnProviderService {
  // injections
  private readonly translateService = inject(TranslateService);
  private readonly filterOptionsService = inject(FilterOptionsService);
  private readonly authorizationService = inject(AuthorizationService);

  private readonly iso3166ToCountryPipe = new Iso3166ToCountryPipe();

  public getColumns(): ColumnDefinitionBase[] {
    const columns: ColumnDefinitionBase[] = [];

    columns.push(
      new TrafficLightColumnDefinition(
        ['progress'],
        'severity',
        'progress',
        this.translateService.instant('pages.transport.tracking-list.columns.progress.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.progress.tooltip'),
        true,
        true,
        new SortInfo('Progress'),
        new LazyMultiSelectFilterInfo(
          'Progress',
          this.translateService.instant('pages.transport.tracking-list.columns.progressDetails.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
        TransportTrafficLightRuleConfig,
        true,
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['severityDetailsDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.severityDetails.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.severityDetails.tooltip'),
        true,
        true,
        new SortInfo('SeverityDetailsDescription'),
        new LazyMultiSelectFilterInfo(
          'SeverityDetails',
          this.translateService.instant('pages.transport.tracking-list.columns.severityDetails.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
        undefined,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['trackingNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.trackingNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.trackingNumber.tooltip'),
        true,
        true,
        new SortInfo('TrackingNumber'),
        new MultiTextFilterInfo('TrackingNumber'),
      ),
    );

    columns.push(
      new DirectionColumnDefinition(
        ['shipmentDirection'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.tooltip'),
        true,
        true,
        new SortInfo('ShipmentDirection'),
        new MultiSelectFilterInfo(
          'ShipmentDirection',
          [
            {
              value: 'CT',
              label: this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.options.cross-shipping'),
              transformedLabel: undefined,
            },
            {
              value: 'IB',
              label: this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.options.inbound'),
              transformedLabel: undefined,
            },
            {
              value: 'OB',
              label: this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.options.outbound'),
              transformedLabel: undefined,
            },
            {
              value: 'RT',
              label: this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.options.return'),
              transformedLabel: undefined,
            },
          ],
          this.translateService.instant('pages.transport.tracking-list.columns.shipmentDirection.placeholder'),
        ),
        {
          crossTraffic: ['CT'],
          inbound: ['IB'],
          outbound: ['OB'],
          returnDirection: ['RT'],
        },
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['clientOrderNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.clientOrderNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.clientOrderNumber.tooltip'),
        true,
        true,
        new SortInfo('ClientOrderNumber'),
        new MultiTextFilterInfo('ClientOrderNumber'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['arvatoDeliveryNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.arvatoDeliveryNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.arvatoDeliveryNumber.tooltip'),
        true,
        true,
        new SortInfo('ArvatoDeliveryNumber'),
        new MultiTextFilterInfo('ArvatoDeliveryNumber'),
      ),
    );

    columns.push(
      new CountryColumnDefinition(
        ['shipToCountry'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToCountry.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToCountry.tooltip'),
        true,
        true,
        new SortInfo('ShipToCountry'),
        new LazyMultiSelectFilterInfo(
          'ShipToCountry',
          this.translateService.instant('pages.transport.tracking-list.columns.shipToCountry.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
          (data: SelectFilterOption) => this.iso3166ToCountryPipe.transform(data.value) ?? data.value,
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['carrierDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.carrier.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.carrier.tooltip'),
        true,
        true,
        new SortInfo('CarrierDescription'),
        new LazyMultiSelectFilterInfo(
          'Carrier',
          this.translateService.instant('pages.transport.tracking-list.columns.carrier.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['serviceCategoryDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.serviceCategory.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.serviceCategory.tooltip'),
        true,
        true,
        new SortInfo('ServiceCategoryDescription'),
        new LazyMultiSelectFilterInfo(
          'ServiceCategory',
          this.translateService.instant('pages.transport.tracking-list.columns.serviceCategory.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['shippingDate'],
        this.translateService.instant('pages.transport.tracking-list.columns.shippingDate.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shippingDate.tooltip'),
        true,
        true,
        new SortInfo('ShippingDate'),
        undefined,
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['eta'],
        this.translateService.instant('pages.transport.tracking-list.columns.eta.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.eta.tooltip'),
        true,
        true,
        new SortInfo('ETA'),
        new DateFilterInfo('ETA'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampDeliveryDate'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampDeliveryDate.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampDeliveryDate.tooltip'),
        true,
        true,
        new SortInfo('TimestampDeliveryDate'),
        new DateFilterInfo('TimestampDeliveryDate'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampFirstDeliveryAttempt'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampFirstDeliveryAttempt.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampFirstDeliveryAttempt.tooltip'),
        true,
        true,
        new SortInfo('TimestampFirstDeliveryAttempt'),
        new DateFilterInfo('TimestampFirstDeliveryAttempt'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['arvatoReference'],
        this.translateService.instant('pages.transport.tracking-list.columns.arvatoReference.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.arvatoReference.tooltip'),
        true,
        true,
        new SortInfo('ArvatoReference'),
        new MultiTextFilterInfo('ArvatoReference'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['bulkMasterNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.bulkMasterNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.bulkMasterNumber.tooltip'),
        true,
        true,
        new SortInfo('BulkMasterNumber'),
        new MultiTextFilterInfo('BulkMasterNumber'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['carrierServiceDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.carrierService.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.carrierService.tooltip'),
        true,
        true,
        undefined,
        new LazyMultiSelectFilterInfo(
          'CarrierService',
          this.translateService.instant('pages.transport.tracking-list.columns.carrierService.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['customerReference'],
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference.tooltip'),
        true,
        true,
        new SortInfo('CustomerReference'),
        new MultiTextFilterInfo('CustomerReference'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['customerReference1'],
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference1.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference1.tooltip'),
        true,
        true,
        new SortInfo('CustomerReference1'),
        new MultiTextFilterInfo('CustomerReference1'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['customerReference2'],
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference2.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.customerReference2.tooltip'),
        true,
        true,
        new SortInfo('CustomerReference2'),
        new MultiTextFilterInfo('CustomerReference2'),
      ),
    );

    columns.push(
      new WeightColumnDefinition(
        ['grossWeight'],
        this.translateService.instant('pages.transport.tracking-list.columns.grossWeight.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.grossWeight.tooltip'),
        true,
        true,
        new SortInfo('GrossWeight'),
        new NumericFilterInfo('GrossWeight', 3, 3),
        'grossWeightUnit',
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['deliveryNoteNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.deliveryNoteNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.deliveryNoteNumber.tooltip'),
        true,
        true,
        new SortInfo('DeliveryNoteNumber'),
        new MultiTextFilterInfo('DeliveryNoteNumber'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampShippingNotification'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampShippingNotification.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampShippingNotification.tooltip'),
        true,
        true,
        new SortInfo('TimestampShippingNotification'),
        new DateFilterInfo('TimestampShippingNotification'),
      ),
    );

    columns.push(
      new BooleanColumnDefinition(
        ['exWorksShipment'],
        this.translateService.instant('pages.transport.tracking-list.columns.exWorksShipment.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.exWorksShipment.tooltip'),
        true,
        true,
        new SortInfo('ExWorksShipment'),
        new BooleanFilterInfo('ExWorksShipment'),
      ),
    );

    columns.push(
      new BooleanColumnDefinition(
        ['newRelease'],
        this.translateService.instant('pages.transport.tracking-list.columns.newRelease.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.newRelease.tooltip'),
        true,
        true,
        new SortInfo('NewRelease'),
        new BooleanFilterInfo('NewRelease'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['otdStatusDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.otdStatus.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.otdStatus.tooltip'),
        true,
        true,
        new SortInfo('OTDStatusDescription'),
        new LazyMultiSelectFilterInfo(
          'OTDStatus',
          this.translateService.instant('pages.transport.tracking-list.columns.otdStatus.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampCarrierEvent'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampCarrierEvent.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampCarrierEvent.tooltip'),
        true,
        true,
        new SortInfo('TimestampCarrierEvent'),
        new DateFilterInfo('TimestampCarrierEvent'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['responsiblePartner'],
        this.translateService.instant('pages.transport.tracking-list.columns.responsiblePartner.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.responsiblePartner.tooltip'),
        true,
        true,
        new SortInfo('ResponsiblePartner'),
        new MultiTextFilterInfo('ResponsiblePartner'),
        undefined,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampFirstIntransitScan'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampFirstIntransitScan.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampFirstIntransitScan.tooltip'),
        true,
        true,
        new SortInfo('TimestampFirstIntransitScan'),
        new DateFilterInfo('TimestampFirstIntransitScan'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampHarmonizedEvent'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampHarmonizedEvent.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampHarmonizedEvent.tooltip'),
        true,
        true,
        new SortInfo('TimestampHarmonizedEvent'),
        new DateFilterInfo('TimestampHarmonizedEvent'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['whHandlingUnit'],
        this.translateService.instant('pages.transport.tracking-list.columns.whHandlingUnit.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.whHandlingUnit.tooltip'),
        true,
        true,
        new SortInfo('WHHandlingUnit'),
        new MultiTextFilterInfo('WHHandlingUnit'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['returnReason'],
        this.translateService.instant('pages.transport.tracking-list.columns.returnReason.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.returnReason.tooltip'),
        true,
        true,
        new SortInfo('ReturnReason'),
        new MultiTextFilterInfo('ReturnReason'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromName1'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName1.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName1.tooltip'),
        true,
        true,
        new SortInfo('ShipFromName1'),
        new MultiTextFilterInfo('ShipFromName1'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromName2'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName2.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName2.tooltip'),
        true,
        true,
        new SortInfo('ShipFromName2'),
        new MultiTextFilterInfo('ShipFromName2'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromName3'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName3.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromName3.tooltip'),
        true,
        true,
        new SortInfo('ShipFromName3'),
        new MultiTextFilterInfo('ShipFromName3'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromCity'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromCity.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromCity.tooltip'),
        true,
        true,
        new SortInfo('ShipFromCity'),
        new MultiTextFilterInfo('ShipFromCity'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromStreet'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromStreet.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromStreet.tooltip'),
        true,
        true,
        new SortInfo('ShipFromStreet'),
        new MultiTextFilterInfo('ShipFromStreet'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipFromZIPCode'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromZIPCode.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromZIPCode.tooltip'),
        true,
        true,
        new SortInfo('ShipFromZIPCode'),
        new MultiTextFilterInfo('ShipFromZIPCode'),
      ),
    );

    columns.push(
      new CountryColumnDefinition(
        ['shipFromCountry'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromCountry.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipFromCountry.tooltip'),
        true,
        true,
        undefined,
        new LazyMultiSelectFilterInfo(
          'ShipFromCountry',
          this.translateService.instant('pages.transport.tracking-list.columns.shipFromCountry.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
          (data: SelectFilterOption) => this.iso3166ToCountryPipe.transform(data.value) ?? data.value,
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['latestCarrierEventDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.latestCarrierEvent.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.latestCarrierEvent.tooltip'),
        true,
        true,
        new SortInfo('LatestCarrierEventDescription'),
        new LazyMultiSelectFilterInfo(
          'LatestCarrierEvent',
          this.translateService.instant('pages.transport.tracking-list.columns.latestCarrierEvent.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['latestHarmonizedEventDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.latestHarmonizedEvent.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.latestHarmonizedEvent.tooltip'),
        true,
        true,
        new SortInfo('LatestHarmonizedEventDescription'),
        new LazyMultiSelectFilterInfo(
          'LatestHarmonizedEvent',
          this.translateService.instant('pages.transport.tracking-list.columns.latestHarmonizedEvent.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['milestoneDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.milestone.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.milestone.tooltip'),
        true,
        true,
        new SortInfo('MilestoneDescription'),
        new LazyMultiSelectFilterInfo(
          'Milestone',
          this.translateService.instant('pages.transport.tracking-list.columns.milestone.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToCity'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToCity.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToCity.tooltip'),
        true,
        true,
        new SortInfo('ShipToCity'),
        new MultiTextFilterInfo('ShipToCity'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToStreet'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToStreet.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToStreet.tooltip'),
        true,
        true,
        new SortInfo('ShipToStreet'),
        new MultiTextFilterInfo('ShipToStreet'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToZIPCode'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToZIPCode.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToZIPCode.tooltip'),
        true,
        true,
        new SortInfo('ShipToZIPCode'),
        new MultiTextFilterInfo('ShipToZIPCode'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToName1'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName1.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName1.tooltip'),
        true,
        true,
        new SortInfo('ShipToName1'),
        new MultiTextFilterInfo('ShipToName1'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToName2'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName2.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName2.tooltip'),
        true,
        true,
        new SortInfo('ShipToName2'),
        new MultiTextFilterInfo('ShipToName2'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipToName3'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName3.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipToName3.tooltip'),
        true,
        true,
        new SortInfo('ShipToName3'),
        new MultiTextFilterInfo('ShipToName3'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['tmFreightOrder'],
        this.translateService.instant('pages.transport.tracking-list.columns.tmFreightOrder.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.tmFreightOrder.tooltip'),
        true,
        true,
        new SortInfo('TMFreightOrder'),
        new MultiTextFilterInfo('TMFreightOrder'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampPlannedInTransit'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampPlannedInTransit.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampPlannedInTransit.tooltip'),
        true,
        true,
        new SortInfo('TimestampPlannedInTransit'),
        new DateFilterInfo('TimestampPlannedInTransit'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['carrierAccountNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.carrierAccountNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.carrierAccountNumber.tooltip'),
        true,
        true,
        new SortInfo('CarrierAccountNumber'),
        new MultiTextFilterInfo('CarrierAccountNumber'),
        undefined,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['resolutionStatus'],
        this.translateService.instant('pages.transport.tracking-list.columns.resolutionStatus.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.resolutionStatus.tooltip'),
        true,
        true,
        new SortInfo('ResolutionStatus'),
        new LazyMultiSelectFilterInfo(
          'ResolutionStatus',
          this.translateService.instant('pages.transport.tracking-list.columns.resolutionStatus.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
          (data: SelectFilterOption) =>
            this.translateService.instant(`pages.transport.tracking-list.columns.resolutionStatus.options.${data.value}`),
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data: any) => {
          const translationPath = `pages.transport.tracking-list.columns.resolutionStatus.options.${data['resolutionStatus']}`;
          const transformedValue = this.translateService.instant(translationPath);

          if (transformedValue == translationPath) {
            return '';
          }

          return transformedValue;
        },
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['shipmentNumber'],
        this.translateService.instant('pages.transport.tracking-list.columns.shipmentNumber.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.shipmentNumber.tooltip'),
        true,
        true,
        new SortInfo('ShipmentNumber'),
        new MultiTextFilterInfo('ShipmentNumber'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['businessUnitDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.businessUnit.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.businessUnit.tooltip'),
        true,
        true,
        undefined,
        new LazyMultiSelectFilterInfo(
          'BusinessUnit',
          this.translateService.instant('pages.transport.tracking-list.columns.businessUnit.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
        undefined,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['businessDescription', 'business'],
        this.translateService.instant('pages.transport.tracking-list.columns.business.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.business.tooltip'),
        true,
        true,
        undefined,
        new LazyMultiSelectFilterInfo(
          'Business',
          this.translateService.instant('pages.transport.tracking-list.columns.business.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
          (data: SelectFilterOption) => `${data.label} (${data.value})`,
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data: any) => `${data['businessDescription']} (${data['business']})`,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampMilestoneReady'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampMilestoneReady.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampMilestoneReady.tooltip'),
        true,
        true,
        new SortInfo('TimestampMilestoneReady'),
        new DateFilterInfo('TimestampMilestoneReady'),
      ),
    );

    columns.push(
      new DateTimeColumnDefinition(
        ['timestampLastUpdate'],
        this.translateService.instant('pages.transport.tracking-list.columns.timestampLastUpdate.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.timestampLastUpdate.tooltip'),
        true,
        true,
        new SortInfo('TimestampLastUpdate'),
        new DateFilterInfo('TimestampLastUpdate'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['customerID'],
        this.translateService.instant('pages.transport.tracking-list.columns.customerID.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.customerID.tooltip'),
        true,
        true,
        new SortInfo('CustomerID'),
        new MultiTextFilterInfo('CustomerID'),
        undefined,
        () => this.authorizationService.hasRole('my-internal'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['siteDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.site.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.site.tooltip'),
        true,
        true,
        new SortInfo('SiteDescription'),
        new LazyMultiSelectFilterInfo(
          'Site',
          this.translateService.instant('pages.transport.tracking-list.columns.site.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new BooleanColumnDefinition(
        ['podAvailability'],
        this.translateService.instant('pages.transport.tracking-list.columns.podAvailability.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.podAvailability.tooltip'),
        true,
        true,
        new SortInfo('PODAvailability'),
        new BooleanFilterInfo('PODAvailability'),
      ),
    );

    columns.push(
      new NumberColumnDefinition(
        ['daysDelayed'],
        this.translateService.instant('pages.transport.tracking-list.columns.daysDelayed.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.daysDelayed.tooltip'),
        true,
        true,
        new SortInfo('DaysDelayed'),
        new NumericFilterInfo('DaysDelayed', 0, 0),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['delayReasonDescription'],
        this.translateService.instant('pages.transport.tracking-list.columns.delayReason.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.delayReason.tooltip'),
        true,
        true,
        new SortInfo('DelayReasonDescription'),
        new LazyMultiSelectFilterInfo(
          'DelayReason',
          this.translateService.instant('pages.transport.tracking-list.columns.delayReason.placeholder'),
          (filter: TableLazyLoadEvent, field: string) => this.filterOptionsService.getFilterOptions(filter, field),
        ),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['distributionChannel'],
        this.translateService.instant('pages.transport.tracking-list.columns.distributionChannel.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.distributionChannel.tooltip'),
        true,
        true,
        new SortInfo('DistributionChannel'),
        new MultiTextFilterInfo('DistributionChannel'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['packagingType'],
        this.translateService.instant('pages.transport.tracking-list.columns.packagingType.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.packagingType.tooltip'),
        true,
        true,
        new SortInfo('PackagingType'),
        new MultiTextFilterInfo('PackagingType'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['lastComment'],
        this.translateService.instant('pages.transport.tracking-list.columns.lastComment.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.lastComment.tooltip'),
        true,
        true,
        undefined,
        undefined,
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['recipientEmail'],
        this.translateService.instant('pages.transport.tracking-list.columns.recipientEmail.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.recipientEmail.tooltip'),
        true,
        true,
        new SortInfo('RecipientEmail'),
        new MultiTextFilterInfo('RecipientEmail'),
      ),
    );

    columns.push(
      new TextColumnDefinition(
        ['recipientPhone'],
        this.translateService.instant('pages.transport.tracking-list.columns.recipientPhone.title'),
        this.translateService.instant('pages.transport.tracking-list.columns.recipientPhone.tooltip'),
        true,
        true,
        new SortInfo('RecipientPhone'),
        new MultiTextFilterInfo('RecipientPhone'),
      ),
    );

    return columns;
  }
}
